export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc'
}

export type SortLabelStyle = 'alpha' | 'numeric' | 'date';

export interface SortableColumn<TKey extends string = string> {
  key: TKey;
  labelStyle: SortLabelStyle;
  /** react-intl message id for the column header name (used as {columnName} in sort labels) */
  columnNameId: string;
  /** Default message for the column header name */
  columnNameDefaultMessage: string;
}

export interface SortConfig<TKey extends string = string> {
  column: TKey;
  direction: SortDirection;
}

export interface RangeFilter {
  from?: number;
  to?: number;
}

export interface DateRangeFilter {
  from?: Date;
  to?: Date;
}

export interface TextMatchFilter {
  /** Selected contact/item IDs or names */
  selectedValues: string[];
}

export interface SelectFilter {
  /** Map of option value → selected boolean */
  selectedValues: Record<string, boolean>;
}

/**
 * Sort label i18n message IDs.
 * Each uses ICU MessageFormat with a `{columnName}` placeholder
 * so translators can reorder words freely.
 */
export const SORT_LABEL_MESSAGE_IDS = {
  alpha: {
    [SortDirection.ASC]: {
      id: 'predict.sort_alpha_asc',
      defaultMessage: '{columnName} A to Z'
    },
    [SortDirection.DESC]: {
      id: 'predict.sort_alpha_desc',
      defaultMessage: '{columnName} Z to A'
    }
  },
  numeric: {
    [SortDirection.ASC]: {
      id: 'predict.sort_numeric_asc',
      defaultMessage: '{columnName} lowest to highest'
    },
    [SortDirection.DESC]: {
      id: 'predict.sort_numeric_desc',
      defaultMessage: '{columnName} highest to lowest'
    }
  },
  date: {
    [SortDirection.ASC]: {
      id: 'predict.sort_date_asc',
      defaultMessage: '{columnName} oldest to newest'
    },
    [SortDirection.DESC]: {
      id: 'predict.sort_date_desc',
      defaultMessage: '{columnName} newest to oldest'
    }
  }
} as const;

// Filter column definition types are defined in the context/types file alongside the specific filter state types for that context, 
// since they often need to be tightly coupled (e.g. to ensure the filter keys match up).
interface BaseColumnDefinition {
  /** Unique key matching the filter state key */
  key: string;
  /** react-intl message id for the filter label */
  labelId: string;
  /** Default message for the filter label */
  labelDefaultMessage: string;
}

export interface TextMatchColumnDefinition extends BaseColumnDefinition {
  type: 'textMatch';
  /** Options for the multi-select */
  options: { value: string; text: string }[];
}

export interface RangeColumnDefinition extends BaseColumnDefinition {
  type: 'range';
  /** Currency symbol for monetary range filters */
  currencySymbol?: string;
}

export interface DateRangeColumnDefinition extends BaseColumnDefinition {
  type: 'dateRange';
  /** Formatted date accessor for date inputs */
  formatDateForInput?: (date: Date) => string;
}

export interface SelectColumnDefinition extends BaseColumnDefinition {
  type: 'select';
  /** Options for the checkbox group */
  options: { value: string; text: string }[];
}

export type FilterColumnDefinition =
  | TextMatchColumnDefinition
  | RangeColumnDefinition
  | DateRangeColumnDefinition
  | SelectColumnDefinition;

// Filter change handlers — the callback signatures used by hooks & sidebar
export interface FilterChangeHandlers {
  handleTextMatchChange: (key: string, selectedValues: string[]) => void;
  handleToggle: (key: string, isActive: boolean) => void;
  handleRangeFromChange: (key: string, value: string) => void;
  handleRangeToChange: (key: string, value: string) => void;
  handleDateRangeFromChange: (key: string, rawValue: string) => void;
  handleDateRangeToChange: (key: string, rawValue: string) => void;
  handleSelectChange: (key: string, optionValue: string, checked: boolean) => void;
}
